import { useState } from "react";
import { Box, Typography, Button, Grid, Container } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BadgeCard from "./BadgeCard";
import BadgeDialog from "./BadgeDialog";
import type { Badge, BadgeFormData } from "./types/badges.types";
import { AddCircleOutlineOutlined } from "@mui/icons-material";

// Initial mock data
const initialBadges: Badge[] = [
  {
    id: "1",
    name: "Module Master",
    description: "Complete all modules in any course",
    icon: "🎯",
    condition: "Complete all modules",
    issueRule: "auto",
  },
  {
    id: "2",
    name: "High Scorer",
    description: "Score above 80% in any assessment",
    icon: "⭐",
    condition: "Score >= 80%",
    issueRule: "auto",
  },
  {
    id: "3",
    name: "Course Completion",
    description: "Finish a complete course",
    icon: "🏆",
    condition: "Complete course",
    issueRule: "auto",
  },
  {
    id: "4",
    name: "Fast Learner",
    description: "Complete course in under 7 days",
    icon: "⚡",
    condition: "Complete in 7 days",
    issueRule: "auto",
  },
  {
    id: "5",
    name: "Perfect Score",
    description: "Get 100% in an assessment",
    icon: "💯",
    condition: "Score = 100%",
    issueRule: "auto",
  },
];

export default function BadgesAndCertificates() {
  const [badges, setBadges] = useState<Badge[]>(initialBadges);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBadge, setEditingBadge] = useState<Badge | null>(null);

  const autoIssueBadges = badges.filter((b) => b.issueRule === "auto");
  const manualIssueBadges = badges.filter((b) => b.issueRule === "manual");

  const handleCreateBadge = () => {
    setEditingBadge(null);
    setDialogOpen(true);
  };

  const handleEditBadge = (badge: Badge) => {
    setEditingBadge(badge);
    setDialogOpen(true);
  };

  const handleDeleteBadge = (badgeId: string) => {
    if (window.confirm("Are you sure you want to delete this badge?")) {
      setBadges(badges.filter((b) => b.id !== badgeId));
    }
  };

  const handleSaveBadge = (badgeData: BadgeFormData) => {
    if (editingBadge) {
      // Update existing badge
      setBadges(
        badges.map((b) =>
          b.id === editingBadge.id ? { ...b, ...badgeData } : b
        )
      );
    } else {
      // Create new badge
      const newBadge: Badge = {
        ...badgeData,
        id: Date.now().toString(),
        createdAt: new Date(),
      };
      setBadges([...badges, newBadge]);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {autoIssueBadges.length} auto-issue badges •{" "}
          {manualIssueBadges.length} manual badges
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineOutlined />}
          onClick={handleCreateBadge}
          sx={{
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Create Badge
        </Button>
      </Box>

      {/* Badges Grid */}
      <Grid container spacing={3}>
        {badges.map((badge) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={badge.id}>
            <BadgeCard
              badge={badge}
              onEdit={handleEditBadge}
              onDelete={handleDeleteBadge}
            />
          </Grid>
        ))}
      </Grid>

      {/* Badge Dialog */}
      <BadgeDialog
        open={dialogOpen}
        badge={editingBadge}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveBadge}
      />
    </Box>
  );
}
